import { useEffect, useMemo, useRef, useState } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { FrameWithPolygon, type FrameBox } from '@/components/ui/frame-with-polygon';
import { ImageLightbox } from '@/components/ui/image-lightbox';
import { Tooltip as UiTooltip } from '@/components/ui/tooltip';

// AI197: общий график распределения «с порогом и фото по бинам» — полка (abs, eff подписью),
// стеллаж (процент), стол (скор), очередь (людей за минуту). Столбцы разрезаны по статусу
// итогового алерта; фото стоят колонками ровно под своими бинами (#1177); маркеры порогов
// перетаскиваются прямо на графике и сохраняются при отпускании (#1184); перетаскивание за
// край расширяет ось в ту сторону в пределах [min,max] (#1186). Клик по фото — лайтбокс
// БЕЗ кропа (feedback_photos_lightbox_never_crop).

export interface DistPhoto {
  key: string;
  url: string;
  caption: string;
  answer: string;
  boxes?: FrameBox[];
  polygons?: number[][][];
  /** Контуры без заливки (зелёные): полка на кропе стеллажа. */
  outlines?: number[][][];
  /** Плашка поверх фото — пересчёт очереди: «5 → 3» (changed), алерт не ушёл бы (alert). */
  badge?: { text: string; tone: 'changed' | 'alert' | 'ok' } | null;
}
export interface DistBin {
  lo: number;
  hi: number;
  /** Первая строка подписи под фото (по умолчанию «lo–hi»). */
  label?: string | null;
  /** Вторая строка подписи (например eff-диапазон). */
  sub?: string | null;
  counts: Record<string, number | undefined>;
  photos: DistPhoto[];
}
export interface DistLegend { label: string; color: string }
export interface DistMarker {
  key: string;
  x: number;
  color: string;
  label: string;
  /** Есть — маркер перетаскивается; вызывается при отпускании. */
  onChange?: (x: number) => void | Promise<void>;
  min?: number;
  max?: number;
  dashed?: boolean;
}

const Y_AXIS_W = 36;
const RIGHT_M = 8;
const MIN_COL = 84;
const CHART_H = 220;

/** Потолок высоты одного фото. Колонки — 1fr на показанный бин: когда бинов с фото мало,
 *  колонка широкая и 5 фото в своих пропорциях вырастали на весь экран — гистограмма уезжала
 *  вниз (Алексей 14.10, #1516). Фото теперь не выше PHOTO_MAX_H: ширина = min(колонка,
 *  высота×пропорция), рамка — по картинке, без подложки вокруг (#1504: никаких «шторок»). */
const PHOTO_MAX_H = 84;

/** Кроп в своих пропорциях: до загрузки 4:3, потом ячейка принимает естественное
 *  соотношение — без «шторок» по бокам (Алексей 12.10, #1504; как ExampleCell, #1372). */
function PhotoCell({ p, borderColor }: { p: DistPhoto; borderColor: string }) {
  const [ar, setAr] = useState<number | null>(null);
  const a = ar ?? 4 / 3;
  return (
    <div
      className="mx-auto overflow-hidden rounded-md border-2 bg-muted"
      style={{ aspectRatio: a, width: `min(100%, ${Math.round(PHOTO_MAX_H * a)}px)`, borderColor }}
    >
      <FrameWithPolygon src={p.url} polygons={p.polygons ?? []} boxes={p.boxes} outlines={p.outlines} alt="" strokeWidth={2} onAspect={setAr} />
    </div>
  );
}

const BADGE_TONE: Record<NonNullable<DistPhoto['badge']>['tone'], string> = {
  changed: 'bg-amber-500 text-white',
  alert: 'bg-red-600 text-white',
  ok: 'bg-slate-700/80 text-white',
};

export default function DistributionChart({
  bins, legend, order, markers = [], xTickFormatter, belowIsBad = true, emptyText, axisMin = 0, axisMax = 1, extendable = true,
}: {
  bins: DistBin[];
  legend: Record<string, DistLegend>;
  order: string[];
  markers?: DistMarker[];
  xTickFormatter?: (v: number) => string;
  /** Красным подписываются бины ниже первого маркера (полка) или выше (очередь/стол). */
  belowIsBad?: boolean;
  emptyText: string;
  /** Пределы расширения оси перетаскиванием (#1186: 0..1 в абсолютных). */
  axisMin?: number;
  axisMax?: number;
  extendable?: boolean;
}) {
  const step = bins.length ? bins[0].hi - bins[0].lo : 0.05;
  const dataLo = bins[0]?.lo ?? 0;
  const dataHi = bins[bins.length - 1]?.hi ?? 1;
  const [ext, setExt] = useState<{ lo: number; hi: number }>({ lo: dataLo, hi: dataHi });
  useEffect(() => { setExt({ lo: dataLo, hi: dataHi }); }, [dataLo, dataHi]);
  const dLo = Math.min(dataLo, ext.lo);
  const dHi = Math.max(dataHi, ext.hi);

  // Бины на всю ось: пустые добавляем слева/справа, чтобы полоса фото и столбцы делили сетку.
  const shown: DistBin[] = useMemo(() => {
    const out: DistBin[] = [];
    for (let x = dLo; x < dataLo - 1e-9; x += step) out.push({ lo: +x.toFixed(4), hi: +(x + step).toFixed(4), counts: {}, photos: [] });
    out.push(...bins);
    for (let x = dataHi; x < dHi - 1e-9; x += step) out.push({ lo: +x.toFixed(4), hi: +(x + step).toFixed(4), counts: {}, photos: [] });
    return out;
  }, [bins, dLo, dHi, dataLo, dataHi, step]);
  const rows = useMemo(() => shown.map((b) => {
    const row: Record<string, number> = { x: (b.lo + b.hi) / 2, lo: b.lo, hi: b.hi };
    for (const k of order) row[k] = b.counts[k] ?? 0;
    return row;
  }), [shown, order]);
  const present = order.filter((k) => bins.some((b) => (b.counts[k] ?? 0) > 0));
  const sumCounts = (b: DistBin) => Object.values(b.counts).reduce<number>((t, v) => t + (v ?? 0), 0);
  const total = bins.reduce((acc, b) => acc + sumCounts(b), 0);
  const photos = useMemo(() => bins.flatMap((b) => b.photos), [bins]);
  const [lb, setLb] = useState<number | null>(null);
  const fmt = xTickFormatter ?? ((v: number) => Number(v).toFixed(2));
  const binLabel = (b: DistBin) => b.label ?? `${fmt(b.lo)}–${fmt(b.hi)}`;
  const first = markers[0];
  const isBad = (b: DistBin) => !!first && (belowIsBad ? b.hi <= first.x : b.lo >= first.x);
  const hasPhotos = photos.length > 0;

  // Перетаскивание маркеров: оверлей поверх области графика, пиксели → значение по домену оси.
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [plotW, setPlotW] = useState(0);
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setPlotW(el.clientWidth - Y_AXIS_W - RIGHT_M));
    ro.observe(el);
    setPlotW(el.clientWidth - Y_AXIS_W - RIGHT_M);
    return () => ro.disconnect();
  }, []);
  const [drag, setDrag] = useState<{ key: string; x: number } | null>(null);
  const toX = (px: number) => dLo + (px / Math.max(plotW, 1)) * (dHi - dLo);
  const toPx = (x: number) => ((x - dLo) / Math.max(dHi - dLo, 1e-9)) * plotW;
  const onPointerDown = (m: DistMarker) => (e: React.PointerEvent) => {
    if (!m.onChange) return;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    setDrag({ key: m.key, x: m.x });
    e.preventDefault();
  };
  const onPointerMove = (m: DistMarker) => (e: React.PointerEvent) => {
    if (!drag || drag.key !== m.key) return;
    const rect = wrapRef.current!.getBoundingClientRect();
    let x = toX(e.clientX - rect.left - Y_AXIS_W);
    x = Math.max(m.min ?? axisMin, Math.min(m.max ?? axisMax, x));
    // #1186: за краем — ось растёт в ту сторону, но не дальше [axisMin, axisMax].
    if (extendable) {
      if (x < dLo + step * 0.25 && dLo - step >= axisMin - 1e-9) setExt((v) => ({ ...v, lo: +(Math.min(v.lo, dLo) - step).toFixed(4) }));
      if (x > dHi - step * 0.25 && dHi + step <= axisMax + 1e-9) setExt((v) => ({ ...v, hi: +(Math.max(v.hi, dHi) + step).toFixed(4) }));
    }
    setDrag({ key: m.key, x });
  };
  const onPointerUp = (m: DistMarker) => () => {
    if (!drag || drag.key !== m.key) return;
    const x = drag.x;
    setDrag(null);
    if (Math.abs(x - m.x) > 1e-6) void m.onChange?.(+x.toFixed(4));
  };

  if (total === 0) return <div className="text-xs text-muted-foreground">{emptyText}</div>;
  return (
    <div className="space-y-2 overflow-x-auto">
      <div ref={wrapRef} className="relative" style={{ minWidth: hasPhotos ? shown.length * MIN_COL + Y_AXIS_W + RIGHT_M : undefined }}>
        {hasPhotos && (
          <div
            className="mb-1 grid gap-1"
            style={{ gridTemplateColumns: `repeat(${shown.length}, minmax(0, 1fr))`, marginLeft: Y_AXIS_W, marginRight: RIGHT_M }}
          >
            {shown.map((b) => (
              <div key={b.lo} className="min-w-0 space-y-1">
                {b.photos.map((p) => (
                  <UiTooltip key={p.key} content={p.caption}><button
                    type="button"
                    onClick={() => setLb(photos.findIndex((q) => q.key === p.key))}
                    className="block w-full"
                  >
                    <span className="relative block">
                      <PhotoCell p={p} borderColor={legend[p.answer]?.color ?? '#999'} />
                      {p.badge && (
                        <span className={`pointer-events-none absolute left-1/2 top-0.5 max-w-full -translate-x-1/2 truncate rounded px-1 text-[9px] font-semibold leading-tight ${BADGE_TONE[p.badge.tone]}`}>
                          {p.badge.text}
                        </span>
                      )}
                    </span>
                  </button></UiTooltip>
                ))}
                {b.photos.length > 0 && (
                  <UiTooltip content={`${binLabel(b)}${b.sub ? ` · ${b.sub}` : ''} · ${sumCounts(b)} замеров`}><div className={`text-[10px] leading-tight ${isBad(b) ? 'text-red-600 dark:text-red-400' : 'text-foreground'}`}>
                    <div className="truncate font-medium">{binLabel(b)}</div>
                    {b.sub && <div className="truncate text-muted-foreground">{b.sub}</div>}
                  </div></UiTooltip>
                )}
              </div>
            ))}
          </div>
        )}
        <div className="relative">
        <ResponsiveContainer width="100%" height={CHART_H}>
          <BarChart accessibilityLayer={false} data={rows} margin={{ top: 22, right: RIGHT_M, bottom: 0, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" strokeOpacity={0.4} />
            <XAxis
              dataKey="x" type="number" domain={[dLo, dHi]}
              ticks={shown.filter((_, i) => i % 2 === 0).map((b) => b.lo)}
              tickFormatter={(v) => fmt(Number(v))}
              tick={{ fontSize: 10, fill: 'var(--color-muted-foreground)' }} axisLine={false} tickLine={false}
            />
            <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: 'var(--color-muted-foreground)' }} axisLine={false} tickLine={false} width={Y_AXIS_W} />
            <Tooltip
              isAnimationActive={false}
              contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid var(--color-border)', background: 'var(--color-card)', color: 'var(--color-foreground)' }}
              labelFormatter={(_, payload) => {
                const p = payload?.[0]?.payload as { lo: number } | undefined;
                const b = p ? shown.find((x) => x.lo === p.lo) : undefined;
                return b ? `${binLabel(b)}${b.sub ? ` · ${b.sub}` : ''}` : '';
              }}
              formatter={(v, name) => [String(v), legend[String(name)]?.label ?? String(name)]}
            />
            {present.map((k) => (
              <Bar key={k} dataKey={k} stackId="a" fill={legend[k]?.color ?? '#999'} isAnimationActive={false} />
            ))}
          </BarChart>
        </ResponsiveContainer>
        {/* Маркеры порогов поверх графика — свои линии, чтобы двигались за пальцем без перерисовки recharts. */}
        <div className="pointer-events-none absolute inset-y-0" style={{ left: Y_AXIS_W, width: plotW, top: 0, height: CHART_H }}>
          {markers.map((m) => {
            const x = drag?.key === m.key ? drag.x : m.x;
            const px = toPx(x);
            if (px < -1 || px > plotW + 1) return null;
            return (
              <div key={m.key} className="absolute top-0 h-full" style={{ left: px }}>
                <div
                  className="absolute top-5 bottom-[26px] w-0 border-l-2"
                  style={{ borderColor: m.color, borderStyle: m.dashed ? 'dashed' : 'solid' }}
                />
                <UiTooltip content={m.onChange ? 'Перетащите — сохранится при отпускании' : undefined}><div
                  className={`pointer-events-auto absolute -top-0.5 -translate-x-1/2 whitespace-nowrap rounded px-1.5 py-0.5 text-[10px] font-medium text-white ${m.onChange ? 'cursor-ew-resize select-none' : ''}`}
                  style={{ background: m.color, touchAction: 'none' }}
                  onPointerDown={onPointerDown(m)}
                  onPointerMove={onPointerMove(m)}
                  onPointerUp={onPointerUp(m)}
                  onPointerCancel={() => setDrag(null)}
                >
                  {drag?.key === m.key ? fmt(x) : m.label}{m.onChange ? ' ⇔' : ''}
                </div></UiTooltip>
                {m.onChange && (
                  <div
                    className="pointer-events-auto absolute top-5 bottom-[26px] -translate-x-1/2 cursor-ew-resize"
                    style={{ width: 14, touchAction: 'none' }}
                    onPointerDown={onPointerDown(m)}
                    onPointerMove={onPointerMove(m)}
                    onPointerUp={onPointerUp(m)}
                    onPointerCancel={() => setDrag(null)}
                  />
                )}
              </div>
            );
          })}
        </div>
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
          <span className="font-medium text-foreground">Статус итогового алерта:</span>
          {present.map((k) => (
            <span key={k} className="flex items-center gap-1">
              <span className="inline-block size-2.5 rounded-sm" style={{ background: legend[k]?.color }} />
              {legend[k]?.label ?? k}
            </span>
          ))}
        </div>
      </div>
      {lb != null && lb >= 0 && (
        <ImageLightbox
          srcs={photos.map((p) => p.url)}
          frameSrcs={photos.map((p) => p.url)}
          polygonsList={photos.map((p) => p.polygons ?? [])}
          boxesList={photos.map((p) => p.boxes ?? null)}
          outlinesList={photos.map((p) => p.outlines ?? null)}
          captions={photos.map((p) => p.caption)}
          index={lb}
          onNavigate={setLb}
          onClose={() => setLb(null)}
        />
      )}
    </div>
  );
}
